import {
  Logo,
  Route,
  RouteContext,
  getMatchMenu,
  getMenuData,
  type MenuDataItem,
} from '@/components/Layout';
import useRouteName from '@/hooks/useRouteName';
import { MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import { Drawer, Menu } from 'antd';
import { ItemType, MenuItemType, SubMenuType } from 'antd/es/menu/interface';
import classNames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import { Helmet, useLocation, useModel } from 'umi';
import styles from './index.less';

export interface SmartLayoutProps {
  className?: string;
  children?: React.ReactNode;
  logo?: React.ReactNode;
  siderWidth?: number;
  rightContent?: React.ReactNode;
  route: Route;
  menuDataRender?: (menuData: MenuDataItem[]) => MenuDataItem[];
  menuItemRender?: (menuData: MenuDataItem) => React.ReactNode;
}

const SmartLayout: React.FC<SmartLayoutProps> = ({
  className = '',
  children,
  logo,
  siderWidth = 210,
  rightContent,
  route,
  menuDataRender,
  menuItemRender,
}) => {
  const location = useLocation();
  const title = useRouteName();
  const { isMobile } = useModel('media');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen((open) => open && isMobile);
  }, [isMobile]);

  const menuInfoData = useMemo<{
    menuData: MenuDataItem[];
  }>(
    () => getMenuData(route?.children || route?.routes || [], menuDataRender),
    [menuDataRender, route?.children, route?.routes],
  );

  const { menuData = [] } = menuInfoData || {};

  const matchMenus = useMemo(() => {
    return getMatchMenu(location.pathname || '/', menuData || [], true);
  }, [location.pathname, menuData]);

  const matchMenuKeys = useMemo(
    () => Array.from(new Set(matchMenus.map((item) => item.key || item.path || ''))),
    [matchMenus],
  );

  const getMenuItems = (menuData: MenuDataItem[]): ItemType[] =>
    menuData
      .filter((item) => !!item)
      .filter((item) => !item.hideInMenu && item.name && item.path)
      .map((item: MenuDataItem) => {
        const menuItem: MenuItemType = {
          key: item.key || (item.path as string),
          label: menuItemRender ? menuItemRender(item) : item.name,
        };
        if (item.children && !item.hideChildrenInMenu)
          (menuItem as SubMenuType).children = getMenuItems(item.children);
        return menuItem;
      });

  const menu = (
    <Menu
      style={{ border: 'none', backgroundColor: 'transparent' }}
      selectedKeys={matchMenuKeys}
      mode={isMobile ? 'inline' : 'horizontal'}
      items={getMenuItems(menuData)}
    />
  );

  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <RouteContext.Provider
        value={{
          isMobile,
          menuData,
          matchMenus,
          matchMenuKeys,
        }}
      >
        <div className={classNames('flex h-full flex-col', className)}>
          <header className="sticky top-0 z-50">
            <div className="box-border flex h-[48px] items-center border-b bg-white px-2 sm:px-4 md:h-[60px] md:px-8">
              {logo}
              <div className="flex-1 overflow-hidden pr-6">
                {isMobile ? (
                  <div className="block cursor-pointer rounded text-xl md:hidden">
                    {open ? (
                      <MenuFoldOutlined onClick={() => setOpen(false)} />
                    ) : (
                      <MenuUnfoldOutlined onClick={() => setOpen(true)} />
                    )}
                  </div>
                ) : (
                  <div className={classNames(styles['menu'], 'hidden md:block')}>{menu}</div>
                )}
              </div>
              {rightContent}
            </div>
          </header>
          <main className="bg-app flex h-full flex-1 flex-col overflow-y-auto">{children}</main>
        </div>
        <Drawer
          title={null}
          placement="left"
          classNames={{ body: '!px-1' }}
          width={siderWidth}
          open={open}
          onClose={() => setOpen(false)}
        >
          <Logo className="mb-2 ml-2 !justify-start pb-4" />
          <div className={styles['menu']}>{menu}</div>
        </Drawer>
      </RouteContext.Provider>
    </>
  );
};

export default SmartLayout;
